import type { CSSProperties } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getToken } from '../lib/api';
import { useI18n } from '../lib/i18n';

type Kind = 'privacy' | 'terms';

const EN = {
  privacyTitle: 'Privacy Policy',
  termsTitle: 'Terms of Service',
  updated: 'Last updated: 30 October 2026',
  privacy: [
    ['Who we are', 'Termix (termix.battletoads.top) is a WEEX Futures + Spot trading desk that runs AI Chips on your behalf after you connect an exchange API key.'],
    ['What we collect', 'Account email and password hash; workspace and chart settings; encrypted WEEX API credentials; trade/fill metadata needed to show positions and volume; basic server logs (IP, timestamps, errors).'],
    ['How credentials are stored', 'API key, secret, and passphrase are encrypted at rest (AES-256-GCM) on our servers. They are never returned to the browser after save. OAuth access and refresh tokens (when enabled) use the same pattern.'],
    ['How we use data', 'To authenticate you, place and manage trades you start, show portfolio and desk UI, improve reliability, and meet broker/security review requirements.'],
    ['Sharing', 'We do not sell personal data. Data is shared with WEEX only as needed to execute authorized API calls, and with subprocessors that host the VPS/database under our control.'],
    ['Retention', 'Account and encrypted keys while the account is active. You may delete keys in Cabinet; closing the account removes associated desk data subject to legal retention.'],
    ['Contact', 'Security / privacy contact: fill before WEEX OAuth submission (see docs/WEEX_OAUTH_SECURITY_REVIEW.md).'],
  ] as Array<[string, string]>,
  terms: [
    ['Service', 'Termix provides tooling to configure and run automated strategies (“AI Chips”) on WEEX USDT-M futures and spot using API keys you authorize. Spot chips are long-only.'],
    ['Your responsibility', 'You are responsible for WEEX account eligibility, API key permissions, IP whitelist, risk settings, and all orders placed while a chip is armed.'],
    ['No advice', 'Indicators and chips are tools, not investment advice. Past sketch/backtest results do not guarantee live performance.'],
    ['API access', 'You may paste API credentials or connect via WEEX OAuth when available. You must keep secrets confidential and revoke keys if compromised.'],
    ['Availability', 'Service is provided as-is during beta. We may suspend access for security, abuse, or maintenance.'],
    ['Liability', 'To the maximum extent permitted by law, Termix is not liable for trading losses, exchange outages, or unauthorized use of keys you connected.'],
    ['Contact', 'Questions: use the security contact listed in the OAuth security packet.'],
  ] as Array<[string, string]>,
};

const RU = {
  privacyTitle: 'Политика конфиденциальности',
  termsTitle: 'Условия использования',
  updated: 'Обновлено: 30 октября 2026',
  privacy: [
    ['Кто мы', 'Termix (termix.battletoads.top) — деск WEEX Futures + Spot: AI-чипы работают от вашего имени после подключения API-ключа.'],
    ['Что собираем', 'Email и хэш пароля; настройки вкладок и графика; зашифрованные ключи WEEX; метаданные сделок для позиций и объёма; служебные логи (IP, время, ошибки).'],
    ['Хранение ключей', 'API key, secret и passphrase шифруются at rest (AES-256-GCM) на сервере и не возвращаются в браузер после сохранения. OAuth access/refresh токены — по той же схеме.'],
    ['Зачем', 'Авторизация, исполнение стратегий, кабинет, надёжность сервиса и требования брокера/security review.'],
    ['Передача', 'Данные не продаём. Передаём в WEEX только то, что нужно для авторизованных API-вызовов; хостинг VPS/БД под нашим контролем.'],
    ['Срок', 'Пока аккаунт активен. Ключи можно удалить в Cabinet; при закрытии аккаунта удаляем связанные данные с учётом законного хранения.'],
    ['Контакт', 'Security / privacy: заполнить до подачи в WEEX OAuth (см. docs/WEEX_OAUTH_SECURITY_REVIEW.md).'],
  ] as Array<[string, string]>,
  terms: [
    ['Сервис', 'Termix даёт инструменты для настройки и запуска автостратегий («AI Chip») на фьючерсах и споте WEEX с вашими API-ключами. На споте — только лонг.'],
    ['Ваша ответственность', 'Допуск к WEEX, права ключа, whitelist IP, риск-настройки и все ордера, пока чип armed.'],
    ['Не консультация', 'Индикаторы и чипы — инструменты, не инвестрекомендации. Скетч/бэктест не гарантирует live-результат.'],
    ['API', 'Можно вставить ключи вручную или подключить WEEX OAuth (когда будет). Секреты храните в тайне; при компрометации отзовите ключ.'],
    ['Доступность', 'На бете — as-is. Можем приостановить доступ по security, abuse или обслуживанию.'],
    ['Ответственность', 'В пределах закона Termix не отвечает за торговые убытки, сбои биржи или несанкционированное использование ваших ключей.'],
    ['Контакт', 'Вопросы — security contact из OAuth security packet.'],
  ] as Array<[string, string]>,
};

export default function LegalPage() {
  const { lang, setLang } = useI18n();
  const loc = useLocation();
  const kind: Kind = loc.pathname.includes('terms') ? 'terms' : 'privacy';
  const copy = lang === 'ru' ? RU : EN;
  const title = kind === 'privacy' ? copy.privacyTitle : copy.termsTitle;
  const sections = kind === 'privacy' ? copy.privacy : copy.terms;
  const backTo = getToken() ? '/' : '/login';

  return (
    <div style={{ height: '100%', overflow: 'auto', maxWidth: 720, margin: '0 auto', padding: '24px 16px 48px', lineHeight: 1.55, boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ fontFamily: 'var(--display)', fontSize: 32, color: 'var(--gold)' }}>{title}</div>
        <Link to={backTo} style={{ color: 'var(--muted)' }}>{getToken() ? '← Desk' : '← Login'}</Link>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, fontSize: 13 }}>
          <Link to="/privacy" style={{ color: kind === 'privacy' ? 'var(--gold)' : 'var(--muted)' }}>Privacy</Link>
          <Link to="/terms" style={{ color: kind === 'terms' ? 'var(--gold)' : 'var(--muted)' }}>Terms</Link>
          <button type="button" onClick={() => setLang('en')} style={chip(lang === 'en')}>EN</button>
          <button type="button" onClick={() => setLang('ru')} style={chip(lang === 'ru')}>RU</button>
        </div>
      </div>
      <p style={{ color: 'var(--muted)', fontSize: 13, marginTop: 0 }}>{copy.updated}</p>
      {sections.map(([h, body]) => (
        <section key={h} style={card}>
          <h2 style={h2}>{h}</h2>
          <p style={{ margin: 0, color: 'var(--text)', fontSize: 14 }}>{body}</p>
        </section>
      ))}
      <p style={{ fontSize: 12, color: 'var(--muted)' }}>
        Termix · WEEX Futures + Spot ·{' '}
        <a href="https://termix.battletoads.top" style={{ color: 'var(--gold)' }}>termix.battletoads.top</a>
      </p>
    </div>
  );
}

const chip = (on: boolean): CSSProperties => ({
  border: `1px solid ${on ? 'var(--gold)' : 'var(--line)'}`,
  background: 'transparent',
  color: on ? 'var(--gold)' : 'var(--muted)',
  borderRadius: 6,
  padding: '2px 8px',
  cursor: 'pointer',
  font: 'inherit',
});

const card: CSSProperties = {
  border: '1px solid var(--line)',
  borderRadius: 10,
  padding: '14px 16px',
  marginBottom: 12,
  background: 'var(--panel)',
};

const h2: CSSProperties = {
  margin: '0 0 8px',
  fontSize: 16,
  fontFamily: 'var(--mono)',
  color: 'var(--gold)',
  fontWeight: 600,
};
